import { useApi } from '@paradigm/api';

import { useGrfqService } from '#/unified-rfqs/contexts/grfqs';
import { Grfq } from '#/unified-rfqs/entities/grfq/domain';
import {
  CreateRfqReq,
  CreateRfqReqLeg,
} from '#/unified-rfqs/repositories/grfq-types';

import { FutureLegView, LegView, OptionLegView } from './leg-view';
import { extractMessage } from './parse-resp-error';
import { useRfqBuilderView, View } from './view';

interface Success {
  readonly ok: true;
  readonly data: Grfq;
}
interface Failure {
  readonly ok: false;
  readonly message: string;
}

export interface GrfqSendCallback {
  (resp: Success | Failure): void;
}

export function useSendBuilderOB() {
  const view = useRfqBuilderView();

  const { isLoading, send } = useSendObBase();

  return {
    isLoading,
    send: (cb: GrfqSendCallback) => {
      if (!view.isValid) throw new TypeError(INVALID_ERROR_MESSAGE);
      const createRfqReq = prepareCreateRfqReq(view);
      send(createRfqReq, cb);
    },
  };
}

export function useSendOB(payload: CreateRfqReq) {
  const { isLoading, send } = useSendObBase();

  return {
    isLoading,
    send: (cb: GrfqSendCallback) => {
      send(payload, cb);
    },
  };
}

function useSendObBase() {
  const { createRfq } = useGrfqService();
  const [{ isLoading }, send] = useApi(createRfq);

  return {
    isLoading,
    send: (payload: CreateRfqReq, cb: GrfqSendCallback) => {
      send(payload, (resp) => {
        if (resp.ok) {
          cb(resp);
        } else {
          const message = extractMessage(resp.data.data) ?? resp.message;
          cb({ ...resp, message });
        }
      });
    },
  };
}

const INVALID_ERROR_MESSAGE =
  'RFQ builder state is not valid. Make sure to' +
  ' only call this function when the state is valid.';

function prepareCreateRfqReq(view: View): CreateRfqReq {
  return {
    venue: view.venue.selectedOption.value,
    legs: view.legs.map(prepareLeg),
  };
}

function prepareLeg(legView: LegView): CreateRfqReqLeg {
  switch (legView.kind) {
    case 'FUTURE':
      return prepareFutureLeg(legView);
    case 'OPTION':
      return prepareUnpricedLeg(legView);
    default:
      throw new TypeError('Leg should be either FUTURE or OPTION');
  }
}

function prepareFutureLeg(legView: FutureLegView): CreateRfqReqLeg {
  const unpricedLeg = prepareUnpricedLeg(legView);
  if (!legView.price.isSupported) return unpricedLeg;

  const hedgeLeg = { ...unpricedLeg, price: legView.price.value };
  return hedgeLeg;
}

function prepareUnpricedLeg(
  legView: FutureLegView | OptionLegView,
): CreateRfqReqLeg {
  return {
    instrument: legView.instrument!,
    ratio: legView.ratio.value,
    side: legView.action.selectedOption,
  };
}
